<template>
    <AppModal :name="name" :onEnterKeyPress="save" :beforeClose="beforeClose" :beforeOpen="beforeOpen">
        <template v-slot:header>
            {{ operation === 'add' ? 'Add' : 'Configure' }} connection
        </template>

        <template v-slot:content>
            <div v-if="isConnectionsFetching" class="text-muted font-16 my-5">
                Loading...
            </div>
            <AppAuthConnectionForm
                v-else
                :appConnectionsList="appConnectionsList"
                :authConnection="authConnection"
                @update="onAuthConnectionUpdate"/>
        </template>

        <template v-if="!isConnectionsFetching" v-slot:actions>
            <div class="row justify-content-end mt-5">
                <div class="col-6 text-left">
                    <button @click="close" class="btn btn-link mr-3">Close</button>
                </div>
                <div class="col-6 text-right">
                    <button v-if="isAddOperation"
                            :disabled="isSaving || isConnecting"
                            @click="save"
                            class="btn btn-primary"
                            style="width: 120px">
                        <span v-show="isSaving">
                            <i class="fas fa-spinner fa-pulse"></i>
                        </span>
                        <span v-show="!isSaving">
                            Add
                        </span>
                    </button>
                    <template v-else-if="isEditOperation">
                        <button v-if="isConnected"
                                v-loading.btn="isDisconnecting"
                                v-tooltip="'Disconnect'"
                                @click="disconnect"
                                class="btn btn-danger mr-2">
                            <span>
                                <i class="fa fa-plug-circle-xmark"></i>
                            </span>
                        </button>
                        <button v-loading.btn="isConnecting"
                                @click="saveAndConnect"
                                class="btn btn-primary"
                                style="width: 150px">
                            <span class="pr-2">
                                <i class="fa fa-plug"></i>
                            </span>
                            Connect
                        </button>
                    </template>
                </div>
            </div>
        </template>
    </AppModal>
</template>

<script>
import axios from 'axios'
import {mapGetters} from 'vuex'
import {ConnectionStatuses, getConnectionStatus} from '@/types/apps/connections'
import {connectApp, disconnectApp} from './index'
import {showErrorMessage, showSuccessMessage} from '@/services/snackbar'
import AppAuthConnectionForm from './AppAuthConnectionForm'

export default {
    name: 'AppAuthConnectionWrite',
    components: {AppAuthConnectionForm},
    props: {
        name: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            operation: 'add',
            appUuid: null,

            authConnection: {
                name: '',
                settings: {
                    appConnectionUuid: null,
                    params: {}
                },
                scopes: [],
                status: ConnectionStatuses.UNKNOWN
            },

            isSaving: false,
            isConnecting: false,
            isDisconnecting: false,

            appConnectionsList: [],
            isConnectionsFetching: false
        }
    },
    computed: {
        ...mapGetters([
            'activeTeamId'
        ]),

        isAddOperation() {
            return this.operation === 'add'
        },

        isEditOperation() {
            return this.operation === 'edit'
        },

        isConnected() {
            return this.authConnection.status === ConnectionStatuses.CONNECTED
        }
    },
    methods: {
        onAuthConnectionUpdate(authConnection) {
            this.authConnection = authConnection
        },

        fetchAppConnections() {
            this.isConnectionsFetching = true
            return axios.get(`automation/workflows/apps/${this.appUuid}/connections`)
                .then(res => {
                    this.appConnectionsList = res.data.data || []
                })
                .finally(() => {
                    this.isConnectionsFetching = false
                })
        },

        save() {
            if (!this.authConnection.name) {
                showErrorMessage('Name is required')
                return
            }
            if (!this.isConnecting) { // always save before connect
                this.isSaving = true
            }
            if (this.operation === 'add') {
                return axios.post(`automation/workflows/connections/${this.appUuid}`, this.authConnection)
                    .then(res => {
                        this.$emit('created', res.data.data)
                        this.close()
                    })
                    .catch(error => {
                        this.$snackbar.show({error})
                    })
                    .finally(() => {
                        this.isSaving = false
                    })
            } else if (this.operation === 'edit') {
                return axios.post(`automation/workflows/connections/${this.appUuid}/${this.authConnection.uuid}`, this.authConnection)
                    .then(res => {
                        this.$emit('updated', res.data.data)
                        // if (!this.isConnecting) {
                        //     this.close()
                        // }
                    })
                    .catch(error => {
                        showErrorMessage(error)
                    })
                    .finally(() => {
                        this.isSaving = false
                    })
            }
        },

        async saveAndConnect() {
            if (!this.authConnection.name) {
                showErrorMessage('Name is required')
                return
            }
            this.isConnecting = true
            const statusKey = await this.connectApp()
            const statusModel = getConnectionStatus(statusKey)
            this.$set(this.authConnection, 'status', statusKey)
            await this.save()
            if (statusModel.label) {
                await this.$snackbar.show({
                    type: statusModel.snackbarType,
                    text: `Connection status: ${statusModel.label}`
                })
            }
            if (statusKey === ConnectionStatuses.CONNECTED) {
                this.close()
            }
            this.isConnecting = false
        },

        close() {
            this.$modal.hide(this.name)
        },

        async beforeOpen({params}) {
            if (!params) {
                return
            }
            this.operation = params.operation
            this.appUuid = params.appUuid
            if (this.isEditOperation && _.get(params.authConnection, 'uuid')) {
                const c = params.authConnection
                this.authConnection.uuid = c.uuid
                this.authConnection.name = c.name
                this.authConnection.status = c.status

                if (c.settings) {
                    if (c.settings.appConnectionUuid) {
                        this.authConnection.settings.appConnectionUuid = c.settings.appConnectionUuid
                    }
                    if (_.isPlainObject(c.settings.params)) {
                        _.merge(this.authConnection.settings.params, c.settings.params)
                    }
                }

                if (_.isArray(c.scopes)) {
                    _.merge(this.authConnection.scopes, c.scopes)
                }
                await this.fetchAppConnections()
            }
        },

        beforeClose() {
            Object.assign(this.$data, this.$options.data.apply(this))
        },

        async connectApp() {
            try {
                const res = await connectApp({
                    appAuthConnectionUuid: this.authConnection.uuid,
                    appConnectionUuid: _.get(this.authConnection, 'settings.appConnectionUuid'),
                    teamUuid: this.activeTeamId,
                    appUuid: this.appUuid,
                    parameters: _.get(this.authConnection, 'settings.params'),
                    scopes: _.isArray(this.authConnection.scopes) ? this.authConnection.scopes : []
                })
                if (_.get(res, 'data.meta.redirect.location')) {
                    window.open(res.data.meta.redirect.location, '_blank')
                }
                return _.get(res, 'data.status') || ConnectionStatuses.CONNECTED
            } catch (e) {
                showErrorMessage()
            }
        },

        async disconnect() {
            try {
                this.isDisconnecting = true
                await disconnectApp(this.authConnection.uuid)
                showSuccessMessage('Disconnected')
                this.close()
            } catch (e) {
                showErrorMessage()
            } finally {
                this.isDisconnecting = false
            }
        }
    }
}
</script>
